import { pageZoom } from './zoom.js';
import {
  forwardRef,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import type { IconData } from './icon-data.js';
import { Icon } from './Icon.js';
import { iconAdd, iconClose } from './generated/icons.js';
import { cx } from './util.js';

export interface FabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  icon: IconData;
  label: string;
  /** Show the label next to the icon. */
  extended?: boolean;
}

/** The one primary action of a screen. In read-only mode keep it disabled and say why nearby. */
export const Fab = forwardRef<HTMLButtonElement, FabProps>(function Fab(
  { icon, label, extended = false, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      aria-label={extended ? undefined : label}
      className={cx('k-fab k-layer', extended && 'k-fab--extended', className)}
    >
      <Icon icon={icon} />
      {extended ? <span className="k-t-label-lg">{label}</span> : null}
    </button>
  );
});

export interface FabMenuItem {
  key: string;
  label: string;
  icon: IconData;
  onSelect: () => void;
}

/** A FAB that opens a short list of actions. Escape closes it and returns focus to the button. */
export function FabMenu({
  items,
  label,
  compact = false,
  labelled = false,
  below = false,
}: {
  items: FabMenuItem[];
  label: string;
  /** Keep the label beside the icon in a compact (rail) button: for the wide rail. */
  labelled?: boolean;
  /** Open the list under the button, lined up with its right edge (a button at the far right of a page). */
  below?: boolean;
  /** Icon only, and the list opens as a popover to the right of the button, over the whole page: for the top of a rail. */
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const button = useRef<HTMLButtonElement>(null);
  const first = useRef<HTMLButtonElement>(null);
  const [pop, setPop] = useState<{ left?: number; right?: number; top: number } | null>(null);
  // In a rail the list cannot live inside it (the rail is narrow and clips): it is placed beside the button.
  useLayoutEffect(() => {
    const el = button.current;
    if (!compact || !open || el === null) return;
    const z = pageZoom();
    const rect = el.getBoundingClientRect();
    if (below) {
      setPop({ right: window.innerWidth / z - rect.right / z, top: rect.bottom / z + 8 });
      return;
    }
    // Beside the rail that holds the button, not just beside the button.
    const edge = el.closest('nav')?.getBoundingClientRect().right ?? rect.right;
    setPop({ left: (Math.max(edge, rect.right) + 8) / z, top: rect.top / z });
  }, [compact, open, below]);
  useEffect(() => {
    if (open) first.current?.focus();
  }, [open]);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  return (
    <div
      className={cx('k-fabmenu', compact && 'k-fabmenu--compact')}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation();
          close();
        }
      }}
    >
      {open ? (
        compact ? (
          createPortal(
            <>
              <div
                className="k-fabmenu__scrim k-fabmenu__scrim--page"
                aria-hidden="true"
                onClick={close}
              />
              <ul
                id={id}
                className={cx('k-fabmenu__items', compact && 'k-fabmenu__pop')}
                role="menu"
                aria-label={label}
                {...(compact && pop !== null ? { style: pop } : {})}
              >
                {items.map((item, index) => (
                  <li
                    key={item.key}
                    role="none"
                    style={{ animationDelay: `${(items.length - index) * 60}ms` }}
                  >
                    <button
                      ref={index === 0 ? first : undefined}
                      type="button"
                      role="menuitem"
                      className="k-fabmenu__item k-layer k-t-label-lg"
                      onClick={() => {
                        close();
                        item.onSelect();
                      }}
                    >
                      <Icon icon={item.icon} size={20} />
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </>,
            document.body,
          )
        ) : (
          <>
            <div className="k-fabmenu__scrim" aria-hidden="true" onClick={close} />
            <ul
              id={id}
              className={cx('k-fabmenu__items', compact && 'k-fabmenu__pop')}
              role="menu"
              aria-label={label}
              {...(compact && pop !== null ? { style: pop } : {})}
            >
              {items.map((item, index) => (
                <li
                  key={item.key}
                  role="none"
                  style={{ animationDelay: `${(items.length - index) * 60}ms` }}
                >
                  <button
                    ref={index === 0 ? first : undefined}
                    type="button"
                    role="menuitem"
                    className="k-fabmenu__item k-layer k-t-label-lg"
                    onClick={() => {
                      close();
                      item.onSelect();
                    }}
                  >
                    <Icon icon={item.icon} size={20} />
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )
      ) : null}
      <button
        ref={button}
        type="button"
        className={cx('k-fab k-layer', open ? 'k-fab--open' : !compact && 'k-fab--extended')}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <Icon icon={open ? iconClose : iconAdd} />
        {labelled || !(open || compact) ? <span className="k-t-label-lg">{label}</span> : null}
      </button>
    </div>
  );
}
